import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// The fields the Qualifying Call column shows. A change in any of them is worth a re-render.
const SHOWN_FIELDS = [
  'status',
  'answeredCount',
  'callStatus',
  'whatsappStatus',
  'callTurns',
  'whatsappTurns',
  'overallScore',
  'scoredAt',
  'recommendation',
  'notInterested',
];

function changed(before, after) {
  if (!before) return true;
  return (
    SHOWN_FIELDS.some((field) => before[field] !== after[field]) ||
    !!before.knockout !== !!after.knockout
  );
}

/**
 * Re-reads the candidates table's Qualifying Agent state on demand, and never on a timer.
 *
 * Until 22 Oct this polled the summary every 5 s while any call was live or any score pending
 * (every 30 s for WhatsApp states), and the replay modal polled the full session on top. A
 * screening left open with one stuck session kept Railway and Mongo busy all day. Recruiters
 * do not watch this screen live, so it is read when someone asks: the Refresh button, or
 * closing a session's card or modal (whose own fetch has just shown newer data than the row).
 *
 * It hits the small summary endpoint and merges only the rows that changed. A session that has
 * just become final triggers one full (quiet) candidates refresh instead, because the row's
 * overall completion depends on it too. Overlapping calls share the request in flight.
 *
 * @param {object} params
 * @param {string} params.screeningId
 * @param {boolean} params.enabled the screening includes the Qualifying Agent
 * @param {Array} params.candidates the table's rows (each may carry `qualifying`)
 * @param {Function} params.setCandidates the rows' state setter
 * @param {Function} params.refetchCandidates fetchCandidates, called with { quiet: true }
 * @returns {{ refresh: Function, refreshing: boolean }}
 */
export default function useQualifyingSummaryRefresh({
  screeningId,
  enabled,
  candidates,
  setCandidates,
  refetchCandidates,
}) {
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const refetchRef = useRef(refetchCandidates);
  refetchRef.current = refetchCandidates;
  const inFlightRef = useRef(null);
  const mountedRef = useRef(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!enabled || !screeningId) return Promise.resolve();
    if (inFlightRef.current) return inFlightRef.current;

    setRefreshing(true);
    const run = (async () => {
      try {
        const res = await axios.get(
          `/api/screenings/${screeningId}/qualifying-agent-sessions/summary`
        );
        if (!mountedRef.current) return;
        const sessions = res.data?.sessions || {};
        let anyChange = false;
        let becameFinal = false;
        for (const candidate of candidatesRef.current || []) {
          const next = sessions[String(candidate.id || candidate._id)];
          if (next && changed(candidate.qualifying, next)) {
            anyChange = true;
            if (next.isTerminal && !candidate.qualifying?.isTerminal) becameFinal = true;
          }
        }
        if (becameFinal) {
          await refetchRef.current?.({ quiet: true });
        } else if (anyChange) {
          setCandidates((rows) =>
            rows.map((row) => {
              const next = sessions[String(row.id || row._id)];
              return next && changed(row.qualifying, next) ? { ...row, qualifying: next } : row;
            })
          );
        }
      } catch {
        // A failed refresh leaves the rows as they were; the recruiter can press Refresh again.
      } finally {
        inFlightRef.current = null;
        if (mountedRef.current) setRefreshing(false);
      }
    })();
    inFlightRef.current = run;
    return run;
  }, [enabled, screeningId, setCandidates]);

  return { refresh, refreshing };
}
